import { useCallback } from "react";

import { useAuth } from "@/auth";
import { useQuery } from "@apollo/client";
import useSetDefaultShippingAddress from "@whoppah/apollo/hooks/useSetDefaultShippingAddress";
import useSetInvoiceAddress from "@whoppah/apollo/hooks/useSetInvoiceAddress";
import meGql from "@whoppah/apollo/query/me.graphql";
import { MeQuery, MeQueryVariables } from "@whoppah/apollo/schema";

export type SaveRolesResult = { ok: true } | { ok: false; message?: string };

/**
 * WHO-5532. The merchant's two address-role pointers plus the two writes.
 *
 * The pointers ride on the `Me` query (`me.merchants[0].default_shipping_address`
 * and `invoice_address`), so this hook shares Apollo's cache, deduplication
 * and loading state with every other `Me` reader; there is no second request
 * and no second copy of the data. Both mutations return the `Merchant`, so a
 * save patches the cache for every consumer at once. `invoiceAddressId` null
 * means "the same as the default delivery address".
 */
export const useAddressRoles = () => {
  const { token } = useAuth();
  const { data, loading, error, refetch } = useQuery<MeQuery, MeQueryVariables>(
    meGql,
    { skip: !token }
  );
  const [postDefaultShippingAddress] = useSetDefaultShippingAddress();
  const [postInvoiceAddress] = useSetInvoiceAddress();

  const merchant = data?.me?.merchants?.[0];

  const setDefaultShippingAddress = useCallback(
    async (addressId: string): Promise<SaveRolesResult> => {
      try {
        await postDefaultShippingAddress({ variables: { addressId } });

        return { ok: true };
      } catch (mutationError) {
        return { ok: false, message: (mutationError as Error).message };
      }
    },
    [postDefaultShippingAddress]
  );

  const setInvoiceAddress = useCallback(
    async (addressId: string | null): Promise<SaveRolesResult> => {
      try {
        await postInvoiceAddress({ variables: { addressId } });

        return { ok: true };
      } catch (mutationError) {
        return { ok: false, message: (mutationError as Error).message };
      }
    },
    [postInvoiceAddress]
  );

  return {
    /** False while signed out, before the first `Me` response, or when it failed. */
    available: !!merchant && !error,
    loading,
    defaultShippingAddressId: merchant?.default_shipping_address?.id ?? null,
    invoiceAddressId: merchant?.invoice_address?.id ?? null,
    refetch,
    setDefaultShippingAddress,
    setInvoiceAddress,
  };
};
